import classNames from "classnames";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider";
import {Section} from "@/components/section/Section.tsx";
import {ServiceField} from "@/lib/constants/service";
import {MonitoringServiceChoices, MonitoringServiceType, ServiceDefinition} from "@/lib/types/checks";
import {CheckVaultServiceField} from "@/pages/checks/checkVaultServiceVariables/CheckVaultServiceField.tsx";
import {CheckFormAutoGeneratedField} from "@/pages/checks/components/form/fields/CheckFormAutoGeneratedField.tsx";
import {AutogeneratedServiceFields, FIELD_TYPES} from "@/pages/checks/constants.ts";
import {fieldIsEnabled, hasCheckSteps, hasVault} from "@/pages/checks/utils.ts";

import styles from "./CheckDetailsForm.module.scss";

interface CheckSettingsFormProps {
  serviceType: MonitoringServiceType;
  service: ServiceDefinition;
  mspChoices: MonitoringServiceChoices;
  testRunnerForm?: null | JSX.Element;
}

export const CheckSettingsForm = ({serviceType, service, mspChoices, testRunnerForm}: CheckSettingsFormProps) => {
  const {userHasFeature} = useCurrentUserContext();
  const serviceFieldsMap = service.fields.reduce(
    (acc, field) => {
      acc[field.field as ServiceField] = field;
      return acc;
    },
    {} as Record<ServiceField, (typeof service.fields)[number]>
  );
  const showVaultVariables = hasVault(service.service_type);

  const generateField = (field: ServiceField) => (
    <CheckFormAutoGeneratedField
      field={serviceFieldsMap[field]}
      versions={service.versions}
      serviceType={service.service_type}
      type={FIELD_TYPES[field as AutogeneratedServiceFields]}
      mspChoices={mspChoices}
    />
  );

  const vaultField = () => {
    if (!showVaultVariables) {
      return null;
    }
    return <CheckVaultServiceField className="mt-4 pulseMaxWidthXL" />;
  };

  return (
    <div className="pulse-color-gray-900">
      <div className="d-flex gap-4">
        <div className="pulseMaxWidthL w-100">
          <div id="settingsKey" className="pulseTextH3Bold">
            Credentials
          </div>

          {service.service_type === MonitoringServiceType.HTTP && (
            <>
              <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthXL">
                <div className="col">{generateField(ServiceField.USERNAME)}</div>
                <div className="col">{generateField(ServiceField.PASSWORD)}</div>
              </div>
              {vaultField()}
              <Section
                title={"Content Validation"}
                description={"Configure request details and response validation"}
                className="pulseMaxWidthXL mt-4"
              />
              <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthXL">
                <div className="col">{generateField(ServiceField.SEND_STRING)}</div>
                <div className="col">{generateField(ServiceField.EXPECT_STRING)}</div>
              </div>
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.EXPECT_STRING_TYPE)}</div>
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.PROXY)}</div>
              <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthXL">
                <div className="col">{generateField(ServiceField.ENCRYPTION)}</div>
                <div className="col">{generateField(ServiceField.STATUS_CODE)}</div>
              </div>
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.HEADERS)}</div>
              {fieldIsEnabled(serviceFieldsMap[ServiceField.HTTP_EXPECTED_CHECKSUM], userHasFeature) && (
                <div className="d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthXL">
                  <div className="col">{generateField(ServiceField.HTTP_EXPECTED_CHECKSUM)}</div>
                  <div className="col">{generateField(ServiceField.HTTP_CHECKSUM_ALGORITHM)}</div>
                </div>
              )}
            </>
          )}
          {serviceType === MonitoringServiceType.FTP && (
            <>
              <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthXL">
                <div className="col">{generateField(ServiceField.USERNAME)}</div>
                <div className="col">{generateField(ServiceField.PASSWORD)} </div>
              </div>
              <div>{generateField(ServiceField.FTP_SKIP_CERT_VERIFICATION)}</div>
              {vaultField()}
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.FTP_PATHS)}</div>
              <div className="mt-2 pulseMaxWidthXL">{generateField(ServiceField.FTP_EXPLICIT_TLS)}</div>
            </>
          )}

          {serviceType === MonitoringServiceType.SFTP && (
            <>
              <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthXL">
                <div className="col">{generateField(ServiceField.USERNAME)}</div>
                <div className="col">{generateField(ServiceField.PASSWORD)}</div>
              </div>
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.SFTP_PRIVATE_KEY)}</div>
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.SFTP_PASSPHRASE)}</div>
              {vaultField()}
              <div className="mt-4 pulseTextH3Bold">Validation & Connection</div>
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.FTP_PATHS)}</div>
              <div className="mt-4 pulseMaxWidthXL">{generateField(ServiceField.SFTP_KNOWN_HOSTS)}</div>
            </>
          )}

          {serviceType === MonitoringServiceType.SMTP && (
            <>
              <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthXL">
                <div className="col">{generateField(ServiceField.USERNAME)}</div>
                <div className="col">{generateField(ServiceField.PASSWORD)}</div>
              </div>
              {vaultField()}
            </>
          )}
          {testRunnerForm && !hasCheckSteps(serviceType) && <div className="d-md-none mt-4">{testRunnerForm}</div>}
        </div>
        {testRunnerForm && !hasCheckSteps(serviceType) && (
          <>
            <div className="d-none d-md-block">
              <Divider orientation="vertical" className="mx-2" />
            </div>
            <div className={classNames(styles.testRunner, "d-none d-md-block")}>{testRunnerForm}</div>
          </>
        )}
      </div>
    </div>
  );
};
